import {
  AWS_REGIONS,
  REGION_CONFIGS,
} from "shell/platform/constants/constants";
import { getCurrentRegion } from "shell/platform/utils/regionalApolloClientUtils";

import { getSessionHeaders } from "./oidcClient";

export interface RestResponse<T = unknown> {
  data: T;
  headers: Headers;
  status: number;
}

const getProxyOrigin = (): string => {
  const region = getCurrentRegion();
  return (
    REGION_CONFIGS[region]?.baseUrl ||
    REGION_CONFIGS[AWS_REGIONS.EU_WEST_2].baseUrl
  );
};

const toStringHeaders = (
  headers: Record<string, string | number>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [key, String(value)]),
  );

const parseResponse = async (response: Response): Promise<unknown> => {
  if (response.status === 204 || response.headers.get("content-length") === "0")
    return;

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("json")) return response.json();
  if (contentType.startsWith("text/")) return response.text();
  return response.blob();
};

const request = async <T>(
  url: string,
  init: RequestInit,
  sessionHeaders: Record<string, string>,
): Promise<T> => {
  const absoluteUrl = /^https?:\/\//i.test(url)
    ? url
    : `${getProxyOrigin()}${url.startsWith("/") ? url : `/${url}`}`;

  const initHeaders = init.headers
    ? Object.fromEntries(new Headers(init.headers).entries())
    : {};
  const headers: Record<string, string> = {
    accept: "application/json",
    ...sessionHeaders,
    ...initHeaders,
  };

  const response = await fetch(absoluteUrl, { ...init, headers });
  const data = await parseResponse(response);

  if (!response.ok) {
    throw data;
  }

  return {
    data,
    status: response.status,
    headers: response.headers,
  } as T;
};

export const restFetch = <T>(url: string, init: RequestInit = {}): Promise<T> =>
  request<T>(url, init, toStringHeaders(getSessionHeaders()));

/**
 * Fetcher for pre-login endpoints (`security: []` in the spec). Never calls
 * getSessionHeaders(), which reloads the page when no user is signed in.
 * Wired per operation via `override.operations` in orval.config.ts.
 */
export const unauthenticatedRestFetch = <T>(
  url: string,
  init: RequestInit = {},
): Promise<T> => request<T>(url, init, {});
